import { useState, useRef, useEffect } from "react";
import { Camera, Cpu, Upload, FileText, Mic, Check, Image, Lightbulb, Play, X, Square, Loader2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface DocumentStepDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: {
    id: number;
    phase: number;
    title: string;
    description: string;
    tools: string[];
  };
  onSave: (data: { notes: string; photos: File[]; voiceNotes: number }) => void;
  onSkip: () => void;
}

const quickTemplates = [
  "Completed as specified - no issues encountered",
  "Minor wear detected - documented in photos",
  "Component replaced - old part removed successfully",
  "Additional cleaning required - completed thoroughly",
];

export function DocumentStepDialog({
  open,
  onOpenChange,
  step,
  onSave,
  onSkip,
}: DocumentStepDialogProps) {
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [activeTab, setActiveTab] = useState("text");
  const [voiceNotes, setVoiceNotes] = useState<Array<{
    id: number;
    duration: string;
    timestamp: string;
    transcription: string;
    audioUrl?: string;
  }>>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordStartRef = useRef<number>(0);
  const timerRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      voiceNotes.forEach((vn) => vn.audioUrl && URL.revokeObjectURL(vn.audioUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1] || "");
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const handleTemplateClick = (template: string) => {
    setNotes((prev) => (prev ? `${prev}\n${template}` : template));
  };

  const handleSave = () => {
    onSave({ notes, photos, voiceNotes: voiceNotes.length });
    setNotes("");
    setPhotos([]);
    setVoiceNotes([]);
  };

  const handleSkip = () => {
    onSkip();
    setNotes("");
    setPhotos([]);
    setVoiceNotes([]);
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPhotos([...photos, ...Array.from(e.target.files)]);
    }
  };

  const stopTimerAndStream = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const handleStartRecording = async () => {
    if (isRecording || isTranscribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) || "";
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const elapsed = Math.max(1, Math.round((Date.now() - recordStartRef.current) / 1000));
        const usedMime = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type: usedMime });
        stopTimerAndStream();
        setIsRecording(false);

        if (blob.size < 1000) {
          toast({ title: "Recording too short", description: "Please record at least a second of audio.", variant: "destructive" });
          return;
        }

        setIsTranscribing(true);
        try {
          const base64 = await blobToBase64(blob);
          const { data, error } = await supabase.functions.invoke("transcribe-audio", {
            body: { audio: base64, mimeType: usedMime },
          });
          if (error) throw error;
          const text = (data?.text as string)?.trim() || "[no speech detected]";
          const audioUrl = URL.createObjectURL(blob);
          setVoiceNotes((prev) => [
            ...prev,
            {
              id: Date.now(),
              duration: formatDuration(elapsed),
              timestamp: new Date().toLocaleTimeString("en-GB"),
              transcription: text,
              audioUrl,
            },
          ]);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : "Transcription failed";
          toast({ title: "Transcription failed", description: message, variant: "destructive" });
        } finally {
          setIsTranscribing(false);
        }
      };

      recordStartRef.current = Date.now();
      setRecordingSeconds(0);
      timerRef.current = window.setInterval(() => {
        setRecordingSeconds(Math.round((Date.now() - recordStartRef.current) / 1000));
      }, 250);
      recorder.start();
      setIsRecording(true);
    } catch (err: unknown) {
      console.error("mic error", err);
      toast({
        title: "Microphone unavailable",
        description: "Please allow microphone access to record voice notes.",
        variant: "destructive",
      });
      stopTimerAndStream();
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
  };

  const handleRemoveVoiceNote = (id: number) => {
    setVoiceNotes((prev) => {
      const target = prev.find((vn) => vn.id === id);
      if (target?.audioUrl) URL.revokeObjectURL(target.audioUrl);
      return prev.filter((vn) => vn.id !== id);
    });
  };

  const handleAddToNotes = (transcription: string) => {
    setNotes((prev) => (prev ? `${prev}\n${transcription}` : transcription));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Camera className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Document Your Work</h2>
              <p className="text-muted-foreground">Step {step.id}: {step.title}</p>
            </div>
          </div>
          <Button variant="outline" className="gap-2">
            <Cpu className="w-4 h-4" />
            AI Assistant
            <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">Available</span>
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left - Photo Upload */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="flex items-center gap-2 mb-4">
                <Image className="w-5 h-5 text-muted-foreground" />
                <h3 className="font-semibold text-foreground">Add Photos</h3>
              </div>

              <label className="block">
                <div className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors">
                  <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="font-medium text-foreground mb-1">Click to upload photos</p>
                  <p className="text-sm text-muted-foreground">or drag and drop images here</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
              </label>

              {photos.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {photos.map((photo, index) => (
                    <div key={index} className="aspect-square rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right - Notes */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="text" className="gap-2">
                    <FileText className="w-4 h-4" />
                    Text Notes
                  </TabsTrigger>
                  <TabsTrigger value="voice" className="gap-2">
                    <Mic className="w-4 h-4" />
                    Voice Notes
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="text" className="mt-0">
                  <label className="block mb-3">
                    <span className="text-sm font-medium text-foreground">Document what you did in this step</span>
                  </label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Describe the work performed, any issues encountered, parts replaced, observations, etc."
                    className="min-h-[120px] mb-4"
                  />

                  <div className="mb-4">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Quick Templates:</p>
                    <div className="space-y-2">
                      {quickTemplates.map((template, index) => (
                        <button
                          key={index}
                          onClick={() => handleTemplateClick(template)}
                          className="w-full text-left p-3 text-sm text-foreground bg-background border border-border rounded-lg hover:border-primary/50 hover:bg-muted/50 transition-colors"
                        >
                          {template}
                        </button>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="voice" className="mt-0">
                  <div className="flex flex-col items-center justify-center py-6">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isRecording ? "bg-destructive/10 animate-pulse" : "bg-muted"}`}>
                      {isTranscribing ? (
                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      ) : (
                        <Mic className={`w-8 h-8 ${isRecording ? "text-destructive" : "text-muted-foreground"}`} />
                      )}
                    </div>
                    <p className="font-medium text-foreground mb-1">
                      {isTranscribing ? "Transcribing…" : isRecording ? `Recording ${formatDuration(recordingSeconds)}` : "Record voice notes"}
                    </p>
                    <p className="text-sm text-muted-foreground text-center mb-4">
                      {isTranscribing
                        ? "Converting your speech to text"
                        : isRecording
                        ? "Tap stop when you're done"
                        : "Tap to start recording your observations"}
                    </p>
                    {isRecording ? (
                      <Button onClick={handleStopRecording} variant="destructive" className="gap-2">
                        <Square className="w-4 h-4" />
                        Stop Recording
                      </Button>
                    ) : (
                      <Button onClick={handleStartRecording} disabled={isTranscribing} className="gap-2 bg-primary hover:bg-primary/90">
                        <Mic className="w-4 h-4" />
                        Start Recording
                      </Button>
                    )}
                  </div>

                  {/* Voice Notes List */}
                  {voiceNotes.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-foreground mb-3">Voice Notes ({voiceNotes.length})</p>
                      <div className="space-y-3">
                        {voiceNotes.map((vn) => (
                          <div key={vn.id} className="bg-background border border-border rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                {vn.audioUrl ? (
                                  <audio src={vn.audioUrl} controls className="h-8" />
                                ) : (
                                  <button className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors">
                                    <Play className="w-4 h-4 text-foreground" />
                                  </button>
                                )}
                                <span className="text-sm text-muted-foreground">{vn.duration}</span>
                                <span className="text-sm text-muted-foreground">{vn.timestamp}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleAddToNotes(vn.transcription)}
                                >
                                  Add to Notes
                                </Button>
                                <button
                                  onClick={() => handleRemoveVoiceNote(vn.id)}
                                  className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                                >
                                  <X className="w-4 h-4 text-muted-foreground" />
                                </button>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground">{vn.transcription}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Transcription Info */}
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg mb-4">
                    <Mic className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      Voice notes are automatically transcribed to English using AI. Tap "Add to Notes" to append a transcription to your written notes.
                    </p>
                  </div>

                  {/* Step Details inside voice tab */}
                  <div className="pt-4 border-t border-border">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Step Details:</p>
                    <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                    <p className="text-sm text-foreground">
                      <strong>Tools used:</strong> {step.tools.join(", ")}
                    </p>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Step Details */}
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm font-medium text-muted-foreground mb-2">Step Details:</p>
                <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                <p className="text-sm text-foreground">
                  <strong>Tools used:</strong> {step.tools.join(", ")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Stats & Actions */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Image className="w-4 h-4" />
                {photos.length} photos
              </span>
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                {notes.length} characters
              </span>
              <span className="flex items-center gap-1">
                <Mic className="w-4 h-4" />
                {voiceNotes.length} voice notes
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleSkip}>
                Skip Documentation
              </Button>
              <Button onClick={handleSave} className="gap-2 bg-primary hover:bg-primary/90">
                <Check className="w-4 h-4" />
                Save & Continue
              </Button>
            </div>
          </div>

          {/* Info Bar */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              Documenting your work with photos, notes, and voice recordings helps create a comprehensive record for the Digital Passport and ensures quality standards. Need help? Ask the AI Assistant!
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
